import { isAfter, parse, startOfMonth } from 'date-fns';
import type { TOptions } from 'i18next';
import { type CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import type { Restrictions } from 'payments-lib-types';
import { always, cond, pipe, replace, T } from 'ramda';
import { luhnAlgorithm } from '@/modules/common/helpers/luhnAlgorithm';
import { MIN_AGE } from './conf';

export type ValidatorMessage = string | [string, TOptions];

export type Validator<T = any, VS = any> = (
  value: T | undefined,
  values?: VS,
  length?: number
) => ValidatorMessage | void;

export function composeValidators<T>(
  ...validators: Validator<T>[]
): Validator<T> {
  return (...args) => {
    for (const validator of validators) {
      const error = validator(...args);

      if (error != null) return error;
    }
  };
}

export const requiredFactory: (msg: ValidatorMessage) => Validator<any> =
  (msg) => (value) =>
    value ? undefined : msg;

export const required = requiredFactory('validation:required');

export function regex<T extends string>(
  re: RegExp,
  msg: ValidatorMessage
): Validator<T> {
  return (value) =>
    value == null ? undefined : re.test(value) ? undefined : msg;
}

export const latinLettersRefCodeRe = /^[a-zA-Z0-9_]+$/;

const nameRe =
  /^[A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F\u0400-\u04FF\u1E00-\u1EFF][A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F\u0400-\u04FF\u1E00-\u1EFF'\u2019. -]*$/;
export const nameRegexValidator = regex(nameRe, 'validation:invalidName');
export const validateName = composeValidators(required, nameRegexValidator);

const passRe = /^.{8,30}$/;
export const passRegexValidator = regex(passRe, '');
export const passValidator = composeValidators(required, passRegexValidator);
export const validatePassword = composeValidators(required, passValidator);

export const emailRe =
  /^((([a-z]|\d|[!#$%&'*+\-/=?^_`{|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#$%&'*+\-/=?^_`{|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;
export const email = regex(emailRe, 'validation:email');
export const validateEmail = composeValidators(required, email);

export const checkboxRequired: Validator<boolean> = (value) =>
  value ? undefined : 'validation:required';

const refCodeRe = /^[A-Za-z0-9_]{3,20}$/;
export const refCodeRegexValidator = regex(
  refCodeRe,
  'validation:referralCode'
);
export const refCodeValidator = composeValidators(
  required,
  refCodeRegexValidator
);

export const dateRe =
  /^((0[1-9]|[12][0-9]|3[01])(\/)(0[13578]|1[02]))|((0[1-9]|[12][0-9])(\/)(02))|((0[1-9]|[12][0-9]|3[0])(\/)(0[469]|11))(\/)\d{4}$/;
export const dateRegexValidator = regex(dateRe, 'validation:invalid');

export const adultValidator =
  (_countryCode?: string): Validator<string> =>
  (date) => {
    if (!date) return;

    const [day, month, year] = date.split('/');
    const myDob = new Date(`${month}/${day}/${year}`);
    const today = new Date();

    const maxDob = new Date(
      today.getFullYear() - MIN_AGE,
      today.getMonth(),
      today.getDate()
    );

    return maxDob.getTime() > myDob.getTime()
      ? undefined
      : `validation:age${MIN_AGE}`;
  };

export const validateDate = (countryCode?: string) =>
  composeValidators(required, dateRegexValidator, adultValidator(countryCode));

export const numberPhoneValidator: Validator<string> = (
  value,
  country?: string
) =>
  value != null &&
  value.length !== 0 &&
  (country == null
    ? isValidPhoneNumber(value.toString())
    : isValidPhoneNumber(value.toString(), country as CountryCode))
    ? undefined
    : 'validation:invalid';

export const maskAmount = (value: string) => {
  const strValue = String(value || '');

  if (strValue === '.') return '0.';

  const sanitizedValue = strValue.replace(/[^\d.]/g, '');
  const [wholePart, decimalPart] = sanitizedValue.split('.');

  if (Number(wholePart) >= 1000) return wholePart;
  if (!decimalPart) return sanitizedValue;

  return `${wholePart}.${decimalPart.replace(/\D/g, '')}`;
};

export const unmaskAmount = (value: string | number) =>
  Number(value.toString().replace(/[^\d.]/g, ''));

export const maskRefCode = (value: string) =>
  value.slice(0, 20).replace(/[^a-zA-Z0-9_]/g, '');

const cardNumberRe = /^[0-9]{4}\s?[0-9]{4}\s?[0-9]{4}(\s?[0-9]{1,7})?$/;
export const cardNumber = regex(cardNumberRe, 'validation:invalid');

export const cardNumberLuhnWarning: Validator<string> = (value) => {
  if (!value) return;
  const digits = value.replace(/\D/g, '');
  if (digits.length < 12) return;
  if (luhnAlgorithm(digits)) return;
  return 'validation:cardNumberLuhnInvalid';
};

const cvvCardRe = /^[0-9]{3,4}$/i;
export const cvvCard = regex(cvvCardRe, 'validation:invalid');

const cardExpiryDateRe = /^(0[1-9]|1[0-2])\/?([0-9]{2})$/i;
const cardExpiryMonthRe = /^(0[1-9]|1[0-2])$/i;
const cardExpiryYearRe = /^([0-9]{2})$/i;

export const cardExpiryDate = regex(cardExpiryDateRe, 'validation:invalid');
export const cardExpiryMonth = regex(cardExpiryMonthRe, 'validation:invalid');
export const cardExpiryYear = regex(cardExpiryYearRe, 'validation:invalid');

type AmountLimitMsg = (restrictions: Restrictions) => ValidatorMessage;

const amountLimitFactory =
  (msgMin: AmountLimitMsg, msgMax: AmountLimitMsg) =>
  (restrictions: Restrictions, unmaskValue?: (v: any) => void) => {
    const toNumber = (value: any) => {
      const updatedValue = unmaskValue ? unmaskValue(value) : value;
      if (updatedValue) {
        return Number(updatedValue.replace(/,/g, '')) || 0;
      }
      return 0;
    };

    const below = (restrictions: Restrictions) => (value: number) =>
      restrictions.minAmountValue != null &&
      restrictions.minAmountValue !== Number.POSITIVE_INFINITY &&
      restrictions.minAmountValue > value;

    const above = (restrictions: Restrictions) => (value: number) =>
      restrictions.maxAmountValue != null &&
      restrictions.maxAmountValue !== Number.POSITIVE_INFINITY &&
      restrictions.maxAmountValue > 0 &&
      restrictions.maxAmountValue < value;

    const checkLimits = (restrictions: Restrictions) =>
      cond<[number], ValidatorMessage | undefined>([
        [below(restrictions), always(msgMin(restrictions))],
        [above(restrictions), always(msgMax(restrictions))],
        [T, always(undefined)],
      ]);

    return pipe(toNumber, checkLimits(restrictions));
  };

const amountLimitMsgMin: AmountLimitMsg = ({
  minAmountValue,
  amountCurrencyCode,
}) => [
  'validation:amount.min',
  { amount: minAmountValue, currency: amountCurrencyCode },
];

const amountLimitMsgMax: AmountLimitMsg = ({
  maxAmountValue,
  amountCurrencyCode,
}) => [
  'validation:amount.max',
  { amount: maxAmountValue, currency: amountCurrencyCode },
];

export const amountLimit = amountLimitFactory(
  amountLimitMsgMin,
  amountLimitMsgMax
);

export const validateMaxAmount = (
  maxAmount: number,
  unmaskValue: (v: any) => any
) => {
  const toNumber = (value: any) => {
    const updatedValue = unmaskValue(value);
    return updatedValue
      ? pipe(replace(/,/g, ''), (s) => Number(s))(updatedValue)
      : 0;
  };

  const above = (maxAmountValue: number) => (value: number) =>
    maxAmountValue !== null &&
    maxAmountValue !== Number.POSITIVE_INFINITY &&
    maxAmountValue > 0 &&
    maxAmountValue < value;

  const checkMaxAmount = (maxAmountValue: number) =>
    cond<[number], ValidatorMessage | undefined>([
      [above(maxAmountValue), always('validation:notEnoughMoney')],
      [T, always(undefined)],
    ]);

  return pipe(toNumber, checkMaxAmount(maxAmount));
};

export const validatePhone = composeValidators(required, numberPhoneValidator);

export const validateCvvCard = composeValidators(required, cvvCard);

export const validateCardNumber = composeValidators(required, cardNumber);

export const validateCardExpiryDate = composeValidators(
  required,
  cardExpiryDate
);
export const validateCardExpiryMonth = composeValidators(
  required,
  cardExpiryMonth
);
export const validateCardExpiryYear = composeValidators(
  required,
  cardExpiryYear
);

const isLaterThanToday = (date: string): boolean => {
  const inputDate = startOfMonth(parse(date, 'MM/yy', new Date()));
  const today = startOfMonth(new Date());

  return isAfter(inputDate, today);
};

export const cardExpiryDateValidator: Validator<string> = (value?: string) =>
  isLaterThanToday(value!) ? undefined : 'validation:expiryDate.invalid';

export const truncateBankNumber = (value: string) =>
  value.replace(/(\s|-)/g, '');

export const getIsValidNumber =
  (
    fieldName: string,
    country: string,
    transformBeforeValidation?: (value: string) => string
  ): Validator<string> =>
  (value) => {
    if (!value) return;
    const transformedValue = transformBeforeValidation
      ? transformBeforeValidation(value)
      : value;
    const num = Number(transformedValue);
    return !isNaN(num) && isFinite(num)
      ? undefined
      : `validation:${country}.${fieldName}.notANumber`;
  };

export const getValidateByLength =
  (
    fieldName: string,
    minLength: number,
    maxLength: number,
    country: string,
    transformBeforeValidation?: (value: string) => string
  ): Validator<string> =>
  (value) => {
    if (!value) return;
    const transformedValue = transformBeforeValidation
      ? transformBeforeValidation(value)
      : value;
    return transformedValue.length >= minLength &&
      transformedValue.length <= maxLength
      ? undefined
      : `validation:${country}.${fieldName}.length`;
  };

export const paymentNameRegexValidator = (
  fieldName: string
): Validator<string> => regex(nameRe, `validation:${fieldName}.invalidName`);
